import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Database,
  RefreshCw,
  Route,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  UserX,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  GetCacheHealth,
  GetCacheStats,
} from "./apis/get";
import {
  PostClearAllCache,
  PostClearCacheByPath,
  PostClearCacheByPattern,
  PostResetCacheBreaker,
} from "./apis/post";
import { DeleteUserCache } from "./apis/delete";

type CacheHealth = {
  redis_alive?: boolean;
  redis_connected?: boolean;
  redis_ok?: boolean;
  is_connected?: boolean;
  ping_ok?: boolean;
  redis_url_set?: boolean;
  client_initialized?: boolean;
  breaker_state?: string;
  circuit_breaker_state?: string;
  breaker_open?: boolean;
  failures?: number;
  failure_count?: number;
  last_failure_at?: string | null;
  [key: string]: unknown;
};

type CacheStats = {
  db_size?: number;
  key_count?: number;
  global_cache_count?: number;
  global_cache_keys?: number;
  user_cache_count?: number;
  user_cache_keys?: number;
  route_cache_count?: number;
  used_memory?: string | number;
  used_memory_human?: string;
  connected_clients?: number;
  is_connected?: boolean;
  [key: string]: unknown;
};

const getResponsePayload = <T,>(response: any): T => {
  if (response?.data && typeof response.data === "object") {
    return response.data as T;
  }
  return (response || {}) as T;
};

const getSuccessFlag = (response: any) => {
  if (typeof response?.status === "boolean") return response.status;
  if (typeof response?.success === "boolean") return response.success;
  if (typeof response?.status === "string") {
    return response.status.toLowerCase() === "success";
  }
  return !response?.detail && !response?.error;
};

const getErrorMessage = (response: any, fallback: string) => {
  if (!response) return fallback;
  if (typeof response === "string") return response;
  if (typeof response?.message === "string") return response.message;
  if (typeof response?.detail === "string") return response.detail;
  if (Array.isArray(response?.detail)) {
    return response.detail
      .map((item: any) =>
        typeof item === "string"
          ? item
          : item?.message || JSON.stringify(item),
      )
      .join(", ");
  }
  return fallback;
};

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "0";
  return String(value);
};

const metricCardClass =
  "rounded-2xl border border-slate-800 bg-slate-900/60 p-5 shadow-[0_0_0_1px_rgba(15,23,42,0.2)]";

export default function CacheManagement() {
  const [health, setHealth] = useState<CacheHealth | null>(null);
  const [stats, setStats] = useState<CacheStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [userId, setUserId] = useState("");
  const [path, setPath] = useState("");
  const [pattern, setPattern] = useState("");
  const [lastResult, setLastResult] = useState<any>(null);

  const breakerState = useMemo(() => {
    if (!health) return "unknown";
    return (
      health.breaker_state ||
      health.circuit_breaker_state ||
      (health.breaker_open ? "open" : "closed") ||
      "unknown"
    );
  }, [health]);

  const redisLive = useMemo(() => {
    if (!health) return false;
    return Boolean(
      health.is_connected ??
        health.ping_ok ??
        health.redis_alive ??
        health.redis_connected ??
        health.redis_ok,
    );
  }, [health]);

  const fetchData = async (silent = false) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const [healthResponse, statsResponse] = await Promise.all([
        GetCacheHealth(),
        GetCacheStats(),
      ]);

      if (!getSuccessFlag(healthResponse)) {
        throw new Error(getErrorMessage(healthResponse, "Failed to load cache health"));
      }
      if (!getSuccessFlag(statsResponse)) {
        throw new Error(getErrorMessage(statsResponse, "Failed to load cache stats"));
      }

      setHealth(getResponsePayload<CacheHealth>(healthResponse));
      setStats(getResponsePayload<CacheStats>(statsResponse));
    } catch (error: any) {
      toast.error(error?.message || "Failed to load cache admin data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const runAction = async (
    actionKey: string,
    request: () => Promise<any>,
    successMessage: string,
    onSuccess?: () => void,
  ) => {
    setActionLoading(actionKey);
    try {
      const response = await request();
      if (!getSuccessFlag(response)) {
        throw new Error(getErrorMessage(response, "Cache action failed"));
      }

      setLastResult(response);
      toast.success(response?.message || successMessage);
      onSuccess?.();
      await fetchData(true);
    } catch (error: any) {
      toast.error(error?.message || "Cache action failed");
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearAll = () => {
    const confirmed = window.confirm(
      "Clear all app cache keys? Presence and pubsub keys will be preserved.",
    );
    if (!confirmed) return;

    runAction(
      "clear-all",
      () => PostClearAllCache(),
      "All application cache entries cleared.",
    );
  };

  const handleClearUser = () => {
    const trimmedUserId = userId.trim();
    if (!trimmedUserId) {
      toast.error("Enter a user ID first.");
      return;
    }

    const confirmed = window.confirm(
      `Clear all cache entries for user ${trimmedUserId}?`,
    );
    if (!confirmed) return;

    runAction(
      "clear-user",
      () => DeleteUserCache(trimmedUserId),
      `Cache cleared for ${trimmedUserId}.`,
      () => setUserId(""),
    );
  };

  const handleClearPath = () => {
    const trimmedPath = path.trim();
    if (!trimmedPath) {
      toast.error("Enter a route prefix first.");
      return;
    }

    runAction(
      "clear-path",
      () => PostClearCacheByPath(trimmedPath),
      `Cache cleared for ${trimmedPath}.`,
      () => setPath(""),
    );
  };

  const handleClearPattern = () => {
    const trimmedPattern = pattern.trim();
    if (!trimmedPattern) {
      toast.error("Enter a Redis glob pattern first.");
      return;
    }
    if (trimmedPattern === "*") {
      toast.error("Wildcard-only patterns are blocked.");
      return;
    }

    const confirmed = window.confirm(
      `Run cache clear using Redis pattern "${trimmedPattern}"?`,
    );
    if (!confirmed) return;

    runAction(
      "clear-pattern",
      () => PostClearCacheByPattern(trimmedPattern),
      `Cache cleared for pattern ${trimmedPattern}.`,
      () => setPattern(""),
    );
  };

  const handleResetBreaker = () => {
    runAction(
      "reset-breaker",
      () => PostResetCacheBreaker(),
      "Cache breaker reset.",
    );
  };

  return (
    <div className="min-h-screen bg-transparent p-4 md:p-8">
      <div className="mx-auto max-w-7xl space-y-8">
        <div className="flex flex-col gap-4 border-b border-slate-800 pb-6 md:flex-row md:items-end md:justify-between">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/80 px-3 py-1 text-[11px] font-black uppercase tracking-[0.22em] text-cyan-300">
              <Database size={14} />
              Cache Admin
            </div>
            <h1 className="text-4xl font-black uppercase tracking-tight text-white">
              Cache Cleanup
            </h1>
            <p className="max-w-3xl text-sm text-slate-400">
              Review Redis health, inspect cache volume, and clear cached data by
              scope without leaving the admin area.
            </p>
          </div>

          <button
            type="button"
            onClick={() => fetchData(true)}
            disabled={refreshing || loading}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-5 text-xs font-black uppercase tracking-[0.2em] text-white transition hover:border-cyan-500/60 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw
              size={15}
              className={refreshing ? "animate-spin" : ""}
            />
            {refreshing ? "Refreshing" : "Refresh"}
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <div className={metricCardClass}>
            <div className="mb-4 flex items-center justify-between">
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">
                Redis
              </span>
              <Activity
                size={16}
                className={redisLive ? "text-emerald-400" : "text-rose-400"}
              />
            </div>
            <div className="text-3xl font-black uppercase text-white">
              {redisLive ? "Live" : "Down"}
            </div>
            <p className="mt-2 text-sm text-slate-400">
              Connectivity reported by the cache health endpoint.
            </p>
          </div>

          <div className={metricCardClass}>
            <div className="mb-4 flex items-center justify-between">
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">
                Breaker
              </span>
              {String(breakerState).toLowerCase() === "closed" ? (
                <ShieldCheck size={16} className="text-emerald-400" />
              ) : (
                <ShieldAlert size={16} className="text-amber-400" />
              )}
            </div>
            <div className="text-3xl font-black uppercase text-white">
              {String(breakerState)}
            </div>
            <p className="mt-2 text-sm text-slate-400">
              Failures:{" "}
              <span className="font-bold text-slate-200">
                {formatValue(health?.failure_count ?? health?.failures)}
              </span>
            </p>
          </div>

          <div className={metricCardClass}>
            <div className="mb-4 flex items-center justify-between">
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">
                Keys
              </span>
              <Database size={16} className="text-cyan-400" />
            </div>
            <div className="text-3xl font-black uppercase text-white">
              {formatValue(stats?.key_count ?? stats?.db_size)}
            </div>
            <p className="mt-2 text-sm text-slate-400">
              Total Redis DB keys visible from the admin stats endpoint.
            </p>
          </div>

          <div className={metricCardClass}>
            <div className="mb-4 flex items-center justify-between">
              <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">
                Memory
              </span>
              <Database size={16} className="text-fuchsia-400" />
            </div>
            <div className="text-3xl font-black uppercase text-white">
              {formatValue(stats?.used_memory_human ?? stats?.used_memory)}
            </div>
            <p className="mt-2 text-sm text-slate-400">
              Connected clients:{" "}
              <span className="font-bold text-slate-200">
                {formatValue(stats?.connected_clients)}
              </span>
            </p>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className={`${metricCardClass} lg:col-span-2`}>
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-black uppercase tracking-wide text-white">
                  Cache Footprint
                </h2>
                <p className="mt-1 text-sm text-slate-400">
                  Quick view of the key groups you can target from admin.
                </p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                  Global Cache
                </div>
                <div className="mt-3 text-2xl font-black text-white">
                  {formatValue(
                    stats?.global_cache_keys ?? stats?.global_cache_count,
                  )}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                  User Cache
                </div>
                <div className="mt-3 text-2xl font-black text-white">
                  {formatValue(
                    stats?.user_cache_keys ?? stats?.user_cache_count,
                  )}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                  Route Cache
                </div>
                <div className="mt-3 text-2xl font-black text-white">
                  {formatValue(stats?.route_cache_count)}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-500">
                  Last Failure
                </div>
                <div className="mt-3 text-sm font-bold text-slate-200">
                  {health?.last_failure_at
                    ? String(health.last_failure_at)
                    : "No recent failure"}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="mt-0.5 text-amber-400" />
              <div>
                <h2 className="text-sm font-black uppercase tracking-[0.2em] text-amber-300">
                  Safety Notes
                </h2>
                <p className="mt-2 text-sm leading-6 text-amber-100/80">
                  Use path and user cleanup first. Pattern cleanup is intentionally
                  left as a power tool, and wildcard-only patterns stay blocked in
                  the UI before the request is sent.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-2">
          <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black uppercase text-white">
                  Clear All App Cache
                </h2>
                <p className="mt-2 text-sm text-slate-400">
                  Wipes `global_cache:*` and `cache:*` while leaving presence and
                  pubsub keys alone.
                </p>
              </div>
              <Trash2 size={20} className="text-rose-400" />
            </div>

            <button
              type="button"
              onClick={handleClearAll}
              disabled={actionLoading === "clear-all"}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-rose-600 px-5 text-xs font-black uppercase tracking-[0.2em] text-white transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {actionLoading === "clear-all" ? (
                <RefreshCw size={15} className="animate-spin" />
              ) : (
                <Trash2 size={15} />
              )}
              Clear All Cache
            </button>
          </section>

          <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black uppercase text-white">
                  Reset Circuit Breaker
                </h2>
                <p className="mt-2 text-sm text-slate-400">
                  Manually close the breaker if Redis has recovered and you want
                  cache usage to resume immediately.
                </p>
              </div>
              <ShieldCheck size={20} className="text-emerald-400" />
            </div>

            <button
              type="button"
              onClick={handleResetBreaker}
              disabled={actionLoading === "reset-breaker"}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 text-xs font-black uppercase tracking-[0.2em] text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {actionLoading === "reset-breaker" ? (
                <RefreshCw size={15} className="animate-spin" />
              ) : (
                <ShieldCheck size={15} />
              )}
              Reset Breaker
            </button>
          </section>

          <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black uppercase text-white">
                  Clear By User
                </h2>
                <p className="mt-2 text-sm text-slate-400">
                  Removes all cache entries scoped to a single user ID.
                </p>
              </div>
              <UserX size={20} className="text-cyan-400" />
            </div>

            <div className="space-y-3">
              <input
                type="text"
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
                placeholder="U221776975882"
                className="h-12 w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-500"
              />
              <button
                type="button"
                onClick={handleClearUser}
                disabled={actionLoading === "clear-user"}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-cyan-600 px-5 text-xs font-black uppercase tracking-[0.2em] text-white transition hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {actionLoading === "clear-user" ? (
                  <RefreshCw size={15} className="animate-spin" />
                ) : (
                  <UserX size={15} />
                )}
                Clear User Cache
              </button>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black uppercase text-white">
                  Clear By Path
                </h2>
                <p className="mt-2 text-sm text-slate-400">
                  Clears cached responses that match a route prefix.
                </p>
              </div>
              <Route size={20} className="text-violet-400" />
            </div>

            <div className="space-y-3">
              <input
                type="text"
                value={path}
                onChange={(event) => setPath(event.target.value)}
                placeholder="/admin/dropdown-data"
                className="h-12 w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-violet-500"
              />
              <button
                type="button"
                onClick={handleClearPath}
                disabled={actionLoading === "clear-path"}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 text-xs font-black uppercase tracking-[0.2em] text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {actionLoading === "clear-path" ? (
                  <RefreshCw size={15} className="animate-spin" />
                ) : (
                  <Route size={15} />
                )}
                Clear Route Cache
              </button>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6 xl:col-span-2">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black uppercase text-white">
                  Clear By Pattern
                </h2>
                <p className="mt-2 text-sm text-slate-400">
                  For targeted Redis glob cleanup when route or user scoping is not
                  enough. Wildcard-only input is blocked.
                </p>
              </div>
              <ShieldAlert size={20} className="text-amber-400" />
            </div>

            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto]">
              <input
                type="text"
                value={pattern}
                onChange={(event) => setPattern(event.target.value)}
                placeholder="global_cache:/community/*"
                className="h-12 w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-amber-500"
              />
              <button
                type="button"
                onClick={handleClearPattern}
                disabled={actionLoading === "clear-pattern"}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-amber-500 px-5 text-xs font-black uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {actionLoading === "clear-pattern" ? (
                  <RefreshCw size={15} className="animate-spin" />
                ) : (
                  <ShieldAlert size={15} />
                )}
                Run Pattern Clear
              </button>
            </div>
          </section>
        </div>

        <section className="rounded-3xl border border-slate-800 bg-slate-900/60 p-6">
          <div className="mb-4">
            <h2 className="text-xl font-black uppercase text-white">
              Last Action Result
            </h2>
            <p className="mt-2 text-sm text-slate-400">
              Handy for checking what the cache endpoints returned most recently.
            </p>
          </div>

          <pre className="max-h-[320px] overflow-auto rounded-2xl border border-slate-800 bg-slate-950/80 p-4 text-xs leading-6 text-slate-300">
            {JSON.stringify(lastResult || { message: "No cache action has been run yet." }, null, 2)}
          </pre>
        </section>
      </div>

      {loading ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm">
          <div className="rounded-2xl border border-slate-800 bg-slate-900 px-6 py-5 text-sm font-bold text-slate-200 shadow-2xl">
            Loading cache admin...
          </div>
        </div>
      ) : null}
    </div>
  );
}
